import React, { useState, useEffect, useMemo } from 'react';
import {
  Timeline,
  Space,
  Form,
  Row,
  Col,
  Button,
  Spin,
} from '@bedrock/components';
import { ChevronUp, ChevronDown } from '@bedrock/icons-react';
import { useMemoizedFn } from 'ahooks';
import classNames from 'classnames/bind';
import styles from './index.less';
import OAProgressTable from './OAProgressTable';
import { ProcessConfig, OADetail, OANode } from './index';
import OAStatusShow from './OAStatusShow';
import POPOChatIcon from './POPOChatIcon';
import CopyIcon from '#components/CopyIcon';
import CurrencyType from '#components/SForm/SFormElement/CurrencyType';

const cx = classNames.bind(styles);

type HistoryItemProps = OADetail & {
  processType: string;
  detailId: string;
  processTypeMap: Record<string, ProcessConfig>;
};

function HistoryItem({
  oa_id,
  oa_url,
  submit_user_name,
  submit_user_email,
  submit_time,
  oa_status,
  oa_reject_reason,
  processType,
  detailId,
  over_amount_reason,
  over_amount_settle_price,
  processTypeMap,
}: HistoryItemProps) {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const processConfig: ProcessConfig = useMemo(() => {
    return processTypeMap[processType] || processTypeMap['default'];
  }, [processType]);

  const [processList, setProcessList] = useState<OANode[]>([]);

  const getProcess = useMemoizedFn(() => {
    const doIt = async () => {
      setShow(true);
      if (loading || processList?.length) return;
      setLoading(true);
      const res = await processConfig.getOaProcessApi({
        oa_id,
        demand_id: +detailId,
        approve_type: processType, // 只有小喇叭方案审批需要传
        demand_type: processConfig.demand_type,
      });
      setProcessList(res?.data?.oa_process_node || []);
      setLoading(false);
    };
    doIt().catch((err) => {
      setLoading(false);
      setShow(false);
    });
  });

  return (
    <div>
      <Form layout="inline" labelAlign="left" showLabelColon={true}>
        <Row
          gutter={[8, 8]}
          style={{
            width: '100%',
          }}
        >
          <Col span={12}>
            <Form.Item label="OA流程单号" name="title">
              <Button
                type="link"
                onClick={() => {
                  oa_url && window.open(oa_url);
                }}
              >
                {oa_id}
              </Button>
              <CopyIcon text={oa_id} tipToolTitle="复制OA流程单号" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="发起人" name="title">
              <span>
                {submit_user_name || '--'}
                {!!submit_user_email && (
                  <POPOChatIcon ssid={submit_user_email} />
                )}
              </span>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="发起时间" name="title">
              <span>{submit_time || '--'}</span>
            </Form.Item>
          </Col>
          {over_amount_settle_price && <Col span={24}>
            <Form.Item label="最终结算金额">
              <CurrencyType value={{ value: over_amount_settle_price, currency: 'rmb' }} currencyColor='yellow' view />
            </Form.Item>
          </Col>}
          {over_amount_reason && <Col span={24}>
            <Form.Item label="超额验收原因" name="title">
              <span>{over_amount_reason}</span>
            </Form.Item>
          </Col>}
          <Col span={24}>
            <Form.Item label="驳回原因" name="title">
              <span style={{ width: 800, display: 'block' }}>{oa_reject_reason || '--'}</span>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="审批详情" name="title">
              <Button
                type="link"
                onClick={() => {
                  if (!show) {
                    getProcess();
                  } else {
                    setShow(!show);
                  }
                }}
              >
                {show ? (
                  <>
                    收起详情
                    <ChevronUp />
                  </>
                ) : (
                  <>
                    查看详情
                    <ChevronDown />
                  </>
                )}
              </Button>
            </Form.Item>
          </Col>
          {show && (
            <Col span={24}>
              <OAProgressTable loading={loading} processList={processList} />
            </Col>
          )}
        </Row>
      </Form>
    </div>
  );
}

type OAProgressHistoryListProps = {
  processType: string;
  detailId: string;
  onError?: (err: any) => void;
  processTypeMap: Record<string, ProcessConfig>;
};

export default function OAProgressHistoryList({
  processType,
  detailId,
  onError,
  processTypeMap,
}: OAProgressHistoryListProps) {
  const [loading, setLoading] = useState(false);
  const [recordData, setRecordData] = useState<OADetail[]>([]);

  const processConfig: ProcessConfig = useMemo(() => {
    return processTypeMap[processType] || processTypeMap['default'];
  }, [processType]);

  const getRecord = useMemoizedFn(() => {
    const doIt = async () => {
      setLoading(true);
      const res = await processConfig.getRecordApi({
        demand_id: +detailId,
        approve_type: processType, // 只有小喇叭方案审批需要传
        demand_type: processConfig.demand_type, // 只有小喇叭方案审批需要传
      });
      setRecordData(res.data);
      setLoading(false);
    };
    doIt().catch((err) => {
      setLoading(false);
      onError?.(err);
    });
  });

  useEffect(() => {
    detailId && getRecord();
  }, [detailId]);

  return (
    <div>
      {loading && (
        <div
          style={{
            width: '100%',
            height: '300px',
          }}
        >
          <Spin.Global
            style={{
              width: '100%',
              height: '100%',
            }}
            spinning={true}
          />
        </div>
      )}
      <Timeline className={cx('timeline')}>
        {recordData?.map?.((item, index) => {
          return (
            <Timeline.Item
              key={index}
              color={index === 0 ? 'primary' : ''}
              status={index === 0 ? 'done' : 'pending'}
              title={
                <Space size={[8, 0]}>
                  第 {recordData.length - index} 次审批
                  {<OAStatusShow oaStatus={item.oa_status} type={'tag'} />}
                </Space>
              }
            >
              <HistoryItem
                processTypeMap={processTypeMap}
                processType={processType}
                detailId={detailId}
                {...item}
              />
            </Timeline.Item>
          );
        })}
      </Timeline>
    </div>
  );
}
